// api.js
import axios from 'axios';

const FALLBACK_DEV_PORT = process.env.REACT_APP_API_PORT || '5001';

const resolveBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window !== 'undefined') {
    const { origin } = window.location;
    if (/localhost:3000$/.test(origin)) {
      return `http://localhost:${FALLBACK_DEV_PORT}`;
    }
    return origin;
  }

  return `http://localhost:${FALLBACK_DEV_PORT}`;
};

let cachedBaseUrl;

export function getApiBaseUrl() {
  if (!cachedBaseUrl) {
    cachedBaseUrl = resolveBaseUrl();
  }
  return cachedBaseUrl;
}

export const API_BASE_URL = getApiBaseUrl();


// Eigene Axios-Instanz ohne festen Content-Type (damit FormData funktioniert)
const service = axios.create({
  baseURL: API_BASE_URL || undefined, // use relative URLs when not set to avoid mixed-content
  timeout: 30000
  // keine default headers hier
});

// Simple concurrency limiter (global, für alle Anfragen)
let inFlight = 0;
// Global cooldown timestamp (ms) used when the server signals rate limiting (429).
// When Date.now() < globalCooldownUntil we pause processing the queue to avoid
// making more requests that will be rejected.
let globalCooldownUntil = 0;
// IMPORTANT:
// A value of 1 serializes *all* requests and causes visible UI stalls
// when many endpoints are loaded in parallel (feed/cards/notifications).
// Keep the default moderate and configurable via env.
const MAX_CONCURRENT_REQUESTS = Number(process.env.REACT_APP_MAX_CONCURRENT_REQUESTS) || 6;
const DEFAULT_TIMEOUT_MS = Number(process.env.REACT_APP_API_TIMEOUT_MS) || 30000;
const UPLOAD_TIMEOUT_MS = Number(process.env.REACT_APP_UPLOAD_TIMEOUT_MS) || 180000;
const requestQueue = [];

function getHeader(headers, key) {
  if (!headers) return '';
  return headers[key] || headers[key.toLowerCase()] || '';
}

function isFormDataPayload(data) {
  return typeof FormData !== 'undefined' && data instanceof FormData;
}

function isUploadRequest(config = {}) {
  const method = String(config?.method || 'GET').toUpperCase();
  if (!['POST', 'PUT', 'PATCH'].includes(method)) return false;

  const hasFormData = isFormDataPayload(config?.data);
  const contentType = String(getHeader(config?.headers, 'Content-Type') || '').toLowerCase();
  const url = String(config?.url || '').toLowerCase();

  const uploadLikePath = /\/(upload|uploads|avatar|cover|gallery|audio)(\/|$)|\/_mine\/upload-|\/profile\/cover-/.test(url);
  return hasFormData || contentType.includes('multipart/form-data') || uploadLikePath;
}

function withTimeout(config = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const currentTimeout = Number(config?.timeout) || 0;
  return {
    ...config,
    timeout: Math.max(currentTimeout, timeoutMs)
  };
}

function finishJob(callback) {
  inFlight = Math.max(0, inFlight - 1);
  try {
    callback();
  } finally {
    processQueue();
  }
}

function runJob(job) {
  const { config, resolve, reject, retries } = job;
  inFlight += 1;
  requestWithRetry(config, retries)
    .then((res) => finishJob(() => resolve(res)))
    .catch((err) => finishJob(() => reject(err)));
}

function processQueue() {
  if (globalCooldownUntil > Date.now()) {
    const wait = Math.max(0, globalCooldownUntil - Date.now());
    setTimeout(processQueue, wait + 50);
    return;
  }

  while (inFlight < MAX_CONCURRENT_REQUESTS && requestQueue.length > 0) {
    const job = requestQueue.shift();
    if (job) {
      runJob(job);
    }
  }
}
function enqueue(config, retries = RETRY_CONFIG.maxRetries) {
  const upload = isUploadRequest(config);
  const preparedConfig = withTimeout(config, upload ? UPLOAD_TIMEOUT_MS : DEFAULT_TIMEOUT_MS);

  // Uploads should not wait behind background polling requests.
  // Also avoid automatic retries to prevent duplicate heavy uploads.
  if (upload) {
    return requestWithRetry(preparedConfig, 0);
  }

  return new Promise((resolve, reject) => {
    requestQueue.push({ config: preparedConfig, resolve, reject, retries });
    // versuche sofort zu starten
    processQueue();
  });
}

// Retry-Konfiguration
const RETRY_CONFIG = {
  maxRetries: 3,
  baseDelay: 1000, // ms
  maxDelay: 30000, // ms
  retryableStatuses: [429, 500, 502, 503, 504],
  logRetries: process.env.NODE_ENV === 'development',
};

// Optional: API-Cache über Service Worker toggeln
const cacheSite = (enabled) => {
  const value = !!enabled;
  if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
    // Direkt über Controller, falls der SW schon läuft
    if (navigator.serviceWorker.controller) {
      navigator.serviceWorker.controller.postMessage({ type: 'SET_API_CACHE', value });
    } else {
      // Falls noch kein Controller vorhanden ist, warte auf readiness
      navigator.serviceWorker.ready.then(reg => {
        reg.active?.postMessage({ type: 'SET_API_CACHE', value });
      }).catch(() => {
        // kein SW verfügbar bzw. noch nicht ready
      });
    }
  }
  // Optionaler lokaler Cache zum Fallback
  try {
    localStorage.setItem('apiCacheEnabled', value ? '1' : '0');
  } catch (e) {
    // Ignore
  }
  return true;
};

function postMessageToSW(message) {
  try {
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      if (navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage(message);
      } else {
        navigator.serviceWorker.ready
          .then((reg) => {
            reg.active?.postMessage(message);
          })
          .catch(() => { });
      }
    }
  } catch (e) {
    // ignore
  }
}

// Helper: Retry-After Header robust parsen (Delta-Sekunden oder HTTP-Date)
function parseRetryAfter(header) {
  if (!header) return null;
  const seconds = parseInt(header, 10);
  if (!isNaN(seconds)) return seconds * 1000;
  const date = Date.parse(header);
  if (!isNaN(date)) return Math.max(0, date - Date.now());
  return null;
}

// Optional: Rate-Limit-Reset aus Header auslesen
function parseRateLimitReset(headers) {
  if (!headers) return null;
  const remaining = parseInt(headers['x-ratelimit-remaining'], 10);
  const reset = parseInt(headers['x-ratelimit-reset'], 10);
  if (!isNaN(reset)) {
    const resetMs = reset > 1e12 ? reset : reset * 1000;
    const delay = resetMs - Date.now();
    return delay > 0 ? delay : 0;
  }
  if (!isNaN(remaining) && remaining === 0) {
    return null;
  }
  return null;
}

// Request-Interceptor: Token aus LocalStorage anhängen
service.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // FormData-Support: Content-Type nur setzen, wenn nicht FormData
    if (config.data instanceof FormData) {
      if (config.headers['Content-Type']) {
        delete config.headers['Content-Type'];
      }
    } else {
      // JSON-Requests: Axios kann das standardmäßig setzen; Optional explizit setzen
      if (!config.headers['Content-Type']) {
        config.headers['Content-Type'] = 'application/json';
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response-Interceptor: 401-Logout + SW cache reset on mutations to users/profiles
service.interceptors.response.use(
  (response) => {
    // On successful mutation, ask SW to clear dynamic caches so Home/feed refresh
    try {
      if (typeof window !== 'undefined') {
        const method = (response?.config?.method || 'GET').toUpperCase();
        if (method !== 'GET') {
          const rawUrl = response?.config?.url || '';
          const path = new URL(rawUrl, window.location.origin).pathname;
          if (/^\/api\/(profiles|users)(\/|$)/.test(path)) {
            postMessageToSW({ type: 'CACHE_RESET' });
          }
        }
      }
    } catch (e) {
      // ignore
    }
    return response;
  },
  (error) => {
    // IMPORTANT:
    // Do not always drop the token on any 401.
    // We only remove auth when the backend indicates an auth problem.
    // This prevents "random" logouts in case a protected endpoint
    // incorrectly returns 401 for transient server/DB issues.
    if (error?.response?.status === 401) {
      try {
        const rawUrl = error?.config?.url || '';
        const method = (error?.config?.method || 'GET').toUpperCase();
        const path = typeof window !== 'undefined'
          ? new URL(rawUrl, window.location.origin).pathname
          : rawUrl;

        const serverError = String(error?.response?.data?.error || '').toLowerCase();
        const isAuthEndpoint = /^\/api\/auth(\/|$)/.test(path);
        const isMeCheck = /^\/api\/auth\/me$/.test(path);

        // We consider it a real "logout" situation only if:
        // - It's the auth check endpoint, OR
        // - Backend explicitly says token invalid, OR
        // - It's an auth endpoint (login/register) where 401 means auth issue.
        const shouldLogout =
          isMeCheck ||
          isAuthEndpoint ||
          serverError.includes('invalid token') ||
          serverError.includes('no token');

        if (shouldLogout) {
          localStorage.removeItem('token');
          // Avoid redirect loops for non-GET requests.
          if (method === 'GET') {
            window.location.href = '/login';
          }
        }
      } catch (e) {
        // Fallback: previous behaviour (safe default)
        localStorage.removeItem('token');
        try {
          window.location.href = '/login';
        } catch (_) { }
      }
    }
    return Promise.reject(error);
  }
);

// Retry-Wrapper (mit Retry-After-Unterstützung)
async function requestWithRetry(config, retries = RETRY_CONFIG.maxRetries) {
  const upload = isUploadRequest(config);
  const retriesLeft = upload ? 0 : retries;
  try {
    return await service(config);
  } catch (err) {
    const status = err.response?.status;
    const shouldRetry =
      (status && RETRY_CONFIG.retryableStatuses.includes(status)) ||
      (!err.response); // Netzwerkfehler
    if (retriesLeft > 0 && shouldRetry) {
      let delayMs = RETRY_CONFIG.baseDelay;
      if (status === 429) {
        const retryAfter = parseRetryAfter(err.response?.headers?.['retry-after']);
        if (retryAfter != null) {
          delayMs = retryAfter;
        } else {
          delayMs = Math.min(
            RETRY_CONFIG.maxDelay,
            Math.pow(2, (RETRY_CONFIG.maxRetries - retriesLeft)) * 1000
          );
        }
        const resetDelay = parseRateLimitReset(err.response?.headers);
        if (resetDelay != null) delayMs = Math.max(delayMs, resetDelay);

        // Enter a global cooldown so other queued requests pause briefly instead of
        // immediately hitting the rate limiter again. Add a small safety margin.
        try {
          globalCooldownUntil = Date.now() + Math.max(1000, delayMs) + 1000;
          if (RETRY_CONFIG.logRetries) {
            console.warn(`Received 429 — activating client cooldown for ${Math.max(1000, delayMs) + 1000}ms`);
          }
        } catch (e) {
          // ignore if globalCooldownUntil isn't writable for any reason
        }
      } else {
        delayMs = Math.min(
          RETRY_CONFIG.maxDelay,
          Math.pow(2, (RETRY_CONFIG.maxRetries - retriesLeft)) * 1000
        );
      }

      delayMs = Math.max(0, delayMs * (0.5 + Math.random() * 0.5));

      if (RETRY_CONFIG.logRetries) {
        console.log(`🔄 Retry attempt ${RETRY_CONFIG.maxRetries - retriesLeft + 1}/${RETRY_CONFIG.maxRetries} in ${delayMs}ms for ${config?.url}`);
        console.log(`❌ Error: ${err.message} (Status: ${err.response?.status ?? 'Network'})`);
      }

      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return requestWithRetry(config, retriesLeft - 1);
    }
    if (RETRY_CONFIG.logRetries && retriesLeft === 0) {
      console.error('💥 Request failed after retries:', err.message);
    }
    throw err;
  }
}

// API Wrapper
const api = {
  get: (url, config = {}) => enqueue({ ...config, method: 'GET', url }),
  post: (url, data, config = {}) => enqueue({ ...config, method: 'POST', url, data }),
  put: (url, data, config = {}) => enqueue({ ...config, method: 'PUT', url, data }),
  delete: (url, config = {}) => enqueue({ ...config, method: 'DELETE', url }),
  patch: (url, data, config = {}) => enqueue({ ...config, method: 'PATCH', url, data }),
  // Direkter Zugriff auf die underlying Axios-Instanz (falls nötig)
  axios: service,
  // Config Getter/Setter (optional)
  getConfig: () => ({ ...RETRY_CONFIG }),
  setConfig: (newConfig) => Object.assign(RETRY_CONFIG, newConfig),
  // Neue Toggle-Funktion
  cacheSite: cacheSite,
};

export default api;
